import { useMemo, useState } from "react"
import { Box, Paper } from "@mui/material"
import { TrendingUp, TrendingDown } from "@mui/icons-material"
import { ErrorBoundary } from "@sentry/react"
import useIntradayPrice from "../hooks/useIntradayPrice"
import { formatTimestamp, formatPrice, getChangeDirection } from "./helpers"
import { ErrorState, WarningBanner, Divider } from "./components"
import { SecurityPriceProps } from "./types"
import SecurityPriceErrorFallback from "./ErrorBoundary"
import "./style.scss"

function SecurityPriceComponent({ modelId, modelProviderId, asOf, enabled = true, refetchInterval }: SecurityPriceProps) {
  const [errorDismissed, setErrorDismissed] = useState(false)
  const { data, isLoading, isError, isFetching, dataUpdatedAt, errorUpdatedAt } = useIntradayPrice({
    modelId,
    modelProviderId,
    asOf,
    enabled,
    refetchInterval,
  })

  const { change, flashClass, changeLabel } = useMemo(() => {
    const pct = parseFloat(data?.change_percent ?? "")
    const change = getChangeDirection(!isNaN(pct) ? pct : null)
    return {
      change,
      flashClass: change === "positive" ? " price-flash-up" : change === "negative" ? " price-flash-down" : "",
      changeLabel: !isNaN(pct) ? `${Math.abs(pct).toFixed(2)}%` : "--",
    }
  }, [data?.change_percent])

  if (isLoading && !errorUpdatedAt) {
    return null
  }

  if (!data && (isError || (isFetching && !!errorUpdatedAt))) {
    if (errorDismissed) return null
    return <ErrorState open onClose={() => setErrorDismissed(true)} />
  }

  if (!data) return null

  return (
    <Box className="components_security-price" component={Paper} square elevation={1}>
      {isError && <WarningBanner lastUpdated={dataUpdatedAt ? formatTimestamp(dataUpdatedAt) : "--"} />}
      <Box className="security-price-content">
        <Box className="data-field">
          <span className="data-field-label">Current Price</span>
          <Box className="price-row">
            <span key={data.price} className={`price-value${flashClass}`}>{formatPrice(data.price)}</span>
            <span className="currency-value">{data.currency || "--"}</span>
          </Box>
        </Box>

        <Divider />

        <Box className="data-field">
          <span className="data-field-label">Intraday Change</span>
          <Box className={`intraday-change-row ${change}`}>
            {change === "positive" && <TrendingUp className="change-icon" />}
            {change === "negative" && <TrendingDown className="change-icon" />}
            <span className="change-value">{changeLabel}</span>
          </Box>
        </Box>

        <Divider />

        <Box className="data-field">
          <span className="data-field-label">Exchange</span>
          <span className="field-value">{data.exchange || "--"}</span>
        </Box>

        <Divider />

        <Box className="data-field">
          <span className="data-field-label">
            Price as of <span className="delayed-disclaimer">(15-minutes delay)</span>
          </span>
          <span className="field-value">{formatTimestamp(data.timestamp)}</span>
        </Box>
      </Box>
    </Box>
  )
}

export default function SecurityPrice(props: SecurityPriceProps) {
  return (
    <ErrorBoundary fallback={<SecurityPriceErrorFallback />}>
      <SecurityPriceComponent {...props} />
    </ErrorBoundary>
  )
}
